import { AccessTokenPayload } from '../../../../core/models/auth/access-token-payload.model';
import { AccessToken } from '../../../../core/models/auth/access-token.model';
import { UtilService } from '../../../../core/services/util.service';
import { Session } from '../../../../core/models/auth/session.model';
import { useAuthStore } from '../../../../core/store/auth.store';
import { useRouteStore } from '../../../../core/store/route.store';
import { AuthService } from '../../services/auth-login.service';
import { PreloadService } from '../../../../core/services/preload.service';
import { ProfileService } from '../../../private/modules/profile/service/profile.service';
import React, { useRef, useState } from 'react';
import { User } from '../../models/user.model';
import { Link, useNavigate } from 'react-router-dom';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Input, Spin } from 'antd';
import type { InputRef } from 'antd';
import { route } from '../../../../constants/routes';
import { jo } from '../../../../theme/css-vars';
import SecurityAvailabilityModal from './SecurityAvailabilityModal';
import TotpModal from './TotpModal';
import SiteBrand from '../../../../core/components/SiteBrand';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { setRoutePrefix } = useRouteStore();
  const [user, setUser] = useState<User>(new User());
  const [loading, setLoading] = useState(false);
  const [securityAvailabilityVisible, setSecurityAvailabilityVisible] =
    useState(false);
  const [totpModalVisible, setTotpModalVisible] = useState(false);
  const [checkingTotp, setCheckingTotp] = useState(false);
  const passwordRef = useRef<InputRef>(null);

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setUser((prevUser) => ({
      ...prevUser,
      [name]: value,
    }));
  };

  const RedirectToKeycloakOtpSetup = () => {
    // First, enable OTP setup for the user via our backend
    AuthService.SetupOtp(user.username)
      .then(() => {
        // After enabling OTP setup, redirect to Keycloak login
        // The user will login first, then be prompted to setup OTP due to the required action
        const keycloakBaseUrl = 'https://user-admin.onrender.com/auth';
        const realm = 'find-job';
        const clientId = 'job-management-client';

        // Use the current React app URL as redirect URI
        const redirectUri = encodeURIComponent(window.location.origin);

        // Create the OAuth2 URL - user will login first, then be prompted for OTP setup
        const keycloakUrl = `${keycloakBaseUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid`;

        // Redirect in same window
        window.location.href = keycloakUrl;
      })
      .catch((error) => {
        console.error('Error setting up OTP:', error);
        UtilService.Alert(
          'Error',
          'error',
          'Unable to setup OTP. Please try again later.'
        );
      });
  };

  const handleSecurityAvailabilityConfigure = () => {
    setSecurityAvailabilityVisible(false);
    // Redirect to OTP setup - login process is cancelled
    RedirectToKeycloakOtpSetup();
  };

  const handleSecurityAvailabilityDismiss = async () => {
    try {
      // Mark as shown for 1 week (7 days)
      await AuthService.MarkSecurityAvailabilityAsShown(user.username, 7);
      setSecurityAvailabilityVisible(false);

      // Now proceed with login process
      Validate(user, Login);
    } catch (error) {
      console.error('Error marking security availability as shown:', error);
      setSecurityAvailabilityVisible(false);
      // Still proceed with login even if marking fails
      Validate(user, Login);
    }
  };

  const handleSecurityAvailabilityClose = async () => {
    try {
      // Mark as shown for 1 month (30 days) - don't show for a month
      await AuthService.MarkSecurityAvailabilityAsShown(user.username, 30);
      setSecurityAvailabilityVisible(false);

      // Now proceed with login process
      Validate(user, Login);
    } catch (error) {
      console.error('Error marking security availability for 1 month:', error);
      setSecurityAvailabilityVisible(false);
      // Still proceed with login even if marking fails
      Validate(user, Login);
    }
  };

  const handleSecurityAvailabilityCancel = () => {
    // Just close the modal without any action - user clicked X button
    setSecurityAvailabilityVisible(false);
    // Don't proceed with login - user cancelled
  };

  const handleTotpSubmit = async (totp: string) => {
    try {
      setLoading(true);
      // Set TOTP in user object and call login
      const userWithTotp = { ...user, totp };
      const item: AccessToken = await AuthService.Login(userWithTotp);

      if (item?.accessToken) {
        localStorage.setItem('token', item.accessToken);

        const payload: AccessTokenPayload = UtilService.DecodeJWT(
          item.accessToken
        );

        const session: Session = {
          session_state: payload.session_state,
          realm_access: payload.realm_access,
          resource_access: payload.resource_access,
          scope: payload.scope,
          sid: payload.sid,
          email_verified: payload.email_verified,
          name: payload.name,
          preferred_username: payload.preferred_username,
          given_name: payload.given_name,
          family_name: payload.family_name,
          email: payload.email,
        };

        setAuth(item, payload, session);
        try {
          const userProfile = await ProfileService.Get();
          if (userProfile?.id && userProfile?.companyId) {
            localStorage.setItem('userProfile', JSON.stringify(userProfile));
            // Set the route prefix based on company name
            setRoutePrefix(userProfile.companyName);
            await PreloadService.preloadAssets(
              userProfile.id,
              userProfile.companyId,
              userProfile.userFile?.file?.id
            );
            sessionStorage.setItem('useDashboardPreloadJobs', 'true');
            sessionStorage.setItem('useDashboardPreloadJobApps', 'true');
          }
        } catch (preloadError) {
          console.warn('Failed to preload assets:', preloadError);
        }
        setTotpModalVisible(false);
        navigate(route('/dashboard'));
      } else {
        console.error('TOTP login failed: No token received');
        UtilService.Alert(
          'Login Failed',
          'error',
          'Failed to get access token. Please try again.'
        );
      }
    } catch (error: any) {
      console.error('TOTP login error:', error);
      if (
        error.message?.includes('INVALID_OTP') ||
        error.message?.includes('401 Unauthorized')
      ) {
        UtilService.Alert(
          'Invalid TOTP',
          'error',
          'The TOTP code you entered is invalid. Please try again.'
        );
      } else {
        UtilService.Alert(
          'Login Error',
          'error',
          'An error occurred during login. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTotpCancel = () => {
    setTotpModalVisible(false);
    // Don't proceed with login - user cancelled
  };

  const handleSignInClick = async () => {
    // Validate required fields first
    if (!user.username.trim()) {
      UtilService.Alert('Important!', 'info', 'You must enter the username.');
      return;
    }

    if (!user.password.trim()) {
      UtilService.Alert('Important!', 'info', 'You must enter the password.');
      return;
    }

    try {
      setCheckingTotp(true);

      // Check if user has TOTP configured
      const totpResponse = await AuthService.CheckTotpStatus(user.username);

      if (totpResponse.hasTotpConfigured) {
        // User has TOTP configured - show TOTP modal
        setTotpModalVisible(true);
        return;
      }

      // User doesn't have TOTP - check security availability
      const securityResponse = await AuthService.CheckSecurityAvailability(
        user.username
      );
      if (securityResponse.shouldShow) {
        // Show security availability modal
        setSecurityAvailabilityVisible(true);
        return;
      }

      // No modals needed - proceed with login
      Login();
    } catch (error) {
      console.error('Error during sign in checks:', error);
      // If checks fail, proceed with login anyway
      Login();
    } finally {
      setCheckingTotp(false);
    }
  };

  const Validate = (user: User, callback: () => void) => {
    const errors: string[] = [];

    if (user.username.length === 0) {
      errors.push('You must enter the username.');
    }

    if (user.password.length === 0) {
      errors.push('You must enter the password.');
    }

    if (errors.length > 0) {
      UtilService.Alert('Important!', 'info', errors.join(' </br> '));

      return;
    }

    callback();
  };

  const Login = async () => {
    try {
      setLoading(true);

      // Proceed with login
      const item: AccessToken = await AuthService.Login(user);

      if (item?.accessToken) {
        localStorage.setItem('token', item.accessToken);

        const payload: AccessTokenPayload = UtilService.DecodeJWT(
          item.accessToken
        );

        const session: Session = {
          session_state: payload.session_state,
          realm_access: payload.realm_access,
          resource_access: payload.resource_access,
          scope: payload.scope,
          sid: payload.sid,
          email_verified: payload.email_verified,
          name: payload.name,
          preferred_username: payload.preferred_username,
          given_name: payload.given_name,
          family_name: payload.family_name,
          email: payload.email,
        };

        setAuth(item, payload, session);
        try {
          const userProfile = await ProfileService.Get();
          if (userProfile?.id && userProfile?.companyId) {
            localStorage.setItem('userProfile', JSON.stringify(userProfile));
            // Set the route prefix based on company name
            setRoutePrefix(userProfile.companyName);
            await PreloadService.preloadAssets(
              userProfile.id,
              userProfile.companyId,
              userProfile.userFile?.file?.id
            );
            sessionStorage.setItem('useDashboardPreloadJobs', 'true');
            sessionStorage.setItem('useDashboardPreloadJobApps', 'true');
          }
        } catch (preloadError) {
          console.warn('Failed to preload assets:', preloadError);
        }
        navigate(route('/dashboard'));
      } else {
        console.error('Login failed: No token received');
      }
    } catch (error: any) {
      console.error('Login error:', error);

      // Handle OTP-related errors
      if (error.message?.includes('OTP_REQUIRED')) {
        // Redirect to OTP verification page with credentials
        navigate('/otp-verification', {
          state: {
            username: user.username,
            password: user.password,
          },
        });
      } else if (error.message?.includes('ACCOUNT_SETUP_REQUIRED')) {
        UtilService.Alert(
          'Account Setup Required',
          'warning',
          'Your account needs to be set up. Please complete the OTP setup first.'
        );
      }
      // Note: Generic login errors are handled by AuthService, no need to show duplicate alerts here
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col justify-center px-6 py-12 lg:px-8">
      <div className="max-w-[320px] w-full m-auto bg-white p-8 rounded-lg shadow-[0_0_0_1px_rgba(0,0,0,0.1),0_4px_12px_rgba(0,0,0,0.15)]">
        <div className="sm:mx-auto w-full">
          <div className="mx-auto flex justify-center">
            <SiteBrand markSize={48} layout="stacked" />
          </div>
          <h2 className="mt-4 text-center text-2xl font-bold tracking-tight text-gray-900">
            Sign in to your account
          </h2>
        </div>

        <div className="mt-8 w-full space-y-8">
          <Input
            id="username"
            name="username"
            type="text"
            placeholder="Username"
            value={user.username}
            onChange={handleInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && passwordRef?.current) {
                passwordRef.current.focus();
              }
            }}
          />
          <Input.Password
            id="password"
            placeholder="Password"
            name="password"
            value={user.password}
            onChange={handleInput}
            ref={passwordRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSignInClick();
              }
            }}
            iconRender={(visible) =>
              visible ? (
                <EyeOutlined style={{ color: jo.primary }} />
              ) : (
                <EyeInvisibleOutlined style={{ color: jo.textDisabled }} />
              )
            }
          />

          <button
            onClick={handleSignInClick}
            disabled={loading || checkingTotp}
            className="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-blue-100 disabled:cursor-not-allowed"
          >
            {loading || checkingTotp ? (
              <div className="flex items-center space-x-2">
                <Spin size="small" />
                <span>{checkingTotp ? 'Checking...' : 'Signing in...'}</span>
              </div>
            ) : (
              'Sign in'
            )}
          </button>
        </div>

        <div className="mt-6 text-right">
          <a
            href="/#/recovery-password"
            className="text-sm text-blue-600 hover:text-blue-500"
          >
            Forgot password?
          </a>
        </div>

        <nav
          className="mt-6 flex items-center justify-center gap-4 border-t border-[var(--jo-border)] pt-4"
          aria-label="Legal"
        >
          <Link
            to="/privacy"
            className="text-xs font-semibold text-[var(--jo-text-muted)] hover:text-[var(--jo-primary)]"
          >
            Privacy
          </Link>
          <Link
            to="/terms"
            className="text-xs font-semibold text-[var(--jo-text-muted)] hover:text-[var(--jo-primary)]"
          >
            Terms
          </Link>
        </nav>
      </div>

      <SecurityAvailabilityModal
        visible={securityAvailabilityVisible}
        onClose={handleSecurityAvailabilityCancel}
        onConfigure={handleSecurityAvailabilityConfigure}
        onDismiss={handleSecurityAvailabilityDismiss}
        onDontShowAgain={handleSecurityAvailabilityClose}
      />

      <TotpModal
        visible={totpModalVisible}
        loading={loading}
        onSubmit={handleTotpSubmit}
        onCancel={handleTotpCancel}
        username={user.username}
      />
    </div>
  );
};

export default Login;
